import { IClient, ClientStatus, IFeeSettingFields, IClinician } from "./types";
import { isSameString } from "./util";

const TBD_FEE_REGEX = /\d+\s*TBD/i;
const TEMP_FEE_REGEX = /\d+(?:\.\d+)?x\d+/i;
const SINGLE_FEE_REGEX = /\b\d+(?:\.\d+)?\b/;
const EVALUATION_FEE_REGEX = /(\d+(?:\.\d+)?)x\d+/;
const UDM_FEE_REGEX = /(\d+(?:\.\d+)?)\s*UDM/i;
const ONGOING_FEE_REGEX = /then\s*(\d+(?:\.\d+)?)/i;

export interface IFeeCalculation {
    single_fee?: string;
    temp_fee?: string;
    temp_no_of_sessions?: string;
    tbd_fee?: string;
    evaluation_fee?: string;
    udm_fee?: string;
    ongoing_fee?: string;
    single_psych_fee?: string;
    // KAP-specific structured fees (used when label/fee_type is "Kap Fee")
    pnp_evaluation_fee?: string;
    therapy_intake_fee?: string;
    kap_initial_fee?: string;
    kap_initial_sessions?: string;
    kap_ongoing_fee?: string;
}

export function formatFeeCalculation(
    inputFee = "",
    label = "",
    departmentFeeType = ""
): string {
    const fees = calculateFees(inputFee, label, departmentFeeType);
    let message = "";
    if (!fees) return message;

    if (label === "Kap Fee" || label === "kap_fee") {
        const {
            pnp_evaluation_fee,
            therapy_intake_fee,
            kap_initial_fee,
            kap_initial_sessions,
            kap_ongoing_fee,
        } = fees;

        if (
            pnp_evaluation_fee &&
            therapy_intake_fee &&
            kap_initial_fee &&
            kap_initial_sessions &&
            kap_ongoing_fee
        ) {
            return `$${pnp_evaluation_fee} for PNP Evaluation; ` +
                `$${therapy_intake_fee} for Therapy Intake; ` +
                `$${kap_initial_fee} for the first ${kap_initial_sessions} ` +
                `KAP sessions, then $${kap_ongoing_fee} per KAP session`;
        }

        if (pnp_evaluation_fee && therapy_intake_fee && kap_ongoing_fee) {
            return `$${pnp_evaluation_fee} for PNP Evaluation; ` +
                `$${therapy_intake_fee} for Therapy Intake; ` +
                `$${kap_ongoing_fee} per KAP session`;
        }
    }

    if (fees.single_psych_fee) {
        return `Client pays $${fees.single_psych_fee}/session`;
    }

    if (fees.evaluation_fee || fees.udm_fee || fees.ongoing_fee) {
        if (fees.udm_fee && fees.ongoing_fee && !fees.evaluation_fee) {
            return `$${fees.udm_fee}/session until your deductible is met and ` +
                `$${fees.ongoing_fee}/session for ongoing sessions`;
        }

        if (fees.evaluation_fee) {
            const evaluationMatch = fees.evaluation_fee.match(
                /^(\d+(?:\.\d+)?)x(\d+)$/
            );
            if (evaluationMatch) {
                const evalAmount = evaluationMatch[1];
                const evalSessions = evaluationMatch[2];
                message += evalSessions === "1"
                    ? `Fee schedule is $${evalAmount} for your evaluation `
                    : `Fee schedule is $${evalAmount}/session for your first ` +
                        `${evalSessions} sessions `;
            } else {
                message += `Fee schedule is $${fees.evaluation_fee} for your evaluation `;
            }
        }
        if (fees.udm_fee) {
            message += `and $${fees.udm_fee}/session until your deductible is met `;
        }
        if (fees.ongoing_fee) {
            message += `and $${fees.ongoing_fee}/session for ongoing sessions `;
        }
        return message.trim();
    }

    const hasTempFee = fees.temp_fee && fees.temp_no_of_sessions;
    if (hasTempFee) {
        message += `Fee schedule is $${fees.temp_fee}/session for the first ` +
            `${fees.temp_no_of_sessions} sessions `;
    }
    if (fees.single_fee) {
        message += `${hasTempFee ? "then" : "Fee schedule is"} ` +
            `$${fees.single_fee}/session `;
    }
    if (fees.tbd_fee) {
        message += `${hasTempFee ? "then" : "Fee schedule is"} ` +
            `$${fees.tbd_fee}/session until their insurance company responds ` +
            "to our first claim ";
    }
    return message;
}

type FeeField =
    | "masters_fee"
    | "dr_fee"
    | "dr_fee_a"
    | "dr_fee_b"
    | "dr_fee_c"
    | "psychiatry_fee"
    | "psychiatry_fee_a"
    | "psychiatry_fee_b"
    | "psychiatry_fee_c"
    | "intern_fee"
    | "lic_fee"
    | "kap_fee"
    | "nutrition_fee"
    | "inn_nutrition_fee"
    | "lic_a_fee"
    | "lic_b_fee"
    | "lic_c_fee"
    | "extern_fee"
    | "inn_masters_fee"
    | "career_fee"
    | "gifted_assessment_fee"
    | "short_term_evaluation_documentation_fee"
    | "testing_fee"
    | "intern_extern_testing_fee";

const FEE_TYPE_BY_FIELD: Record<FeeField, string> = {
    masters_fee: "Masters Fee",
    dr_fee: "Dr Fee",
    dr_fee_a: "Dr Fee A",
    dr_fee_b: "Dr Fee B",
    dr_fee_c: "Dr Fee C",
    psychiatry_fee: "Psych Fee",
    psychiatry_fee_a: "Psych A Fee",
    psychiatry_fee_b: "Psych B Fee",
    psychiatry_fee_c: "Psych C Fee",
    intern_fee: "Intern Fee",
    lic_fee: "Lic Fee",
    kap_fee: "Kap Fee",
    nutrition_fee: "Nutrition Fee",
    inn_nutrition_fee: "Inn Nutrition Fee",
    lic_a_fee: "Lic Fee A",
    lic_b_fee: "Lic Fee B",
    lic_c_fee: "Lic Fee C",
    extern_fee: "Extern Fee",
    inn_masters_fee: "Inn Masters Fee",
    career_fee: "Career Fee",
    gifted_assessment_fee: "Gifted Assessment Fee",
    short_term_evaluation_documentation_fee:
        "Short Term Evaluation & Documentation Fee",
    testing_fee: "Testing Fee",
    intern_extern_testing_fee: "Intern/Extern Testing Fee",
};

export const getApplicableFee = (
    client: Pick<IClient, ClientStatus.FEE_SETTING | "clinician_fees">,
    credential: string,
    clinicianEmail: string,
    requestedService?: string,
    department_fee_type?: string
) => {
    let fee_to_use: string;
    let fee_type: string;

    // Get general fee settings as base
    const generalFees: IFeeSettingFields = client[ClientStatus.FEE_SETTING] || {
        masters_fee: "",
        dr_fee: "",
        dr_fee_a: "",
        dr_fee_b: "",
        dr_fee_c: "",
        psychiatry_fee: "",
        psychiatry_fee_a: "",
        psychiatry_fee_b: "",
        psychiatry_fee_c: "",
        intern_fee: "",
        lic_fee: "",
        kap_fee: "",
        lic_a_fee: "",
        lic_b_fee: "",
        lic_c_fee: "",
        extern_fee: "",
        career_fee: "",
        gifted_assessment_fee: "",
        short_term_evaluation_documentation_fee: "",
        nutrition_fee: "",
        testing_fee: "",
        intern_extern_testing_fee: "",
        is_self_pay: false,
        inn_masters_fee: "",
        inn_nutrition_fee: "",
    };

    // Track which fee source was used for the specific fee being returned
    let fee_source_used = "general";

    const clinicianFeesForEmail = clinicianEmail
        ? client.clinician_fees?.[clinicianEmail]
        : undefined;

    const getFeeResult = (fields: FeeField[]) => {
        for (const field of fields) {
            const clinicianFee = clinicianFeesForEmail?.[field];
            if (clinicianFee) {
                return {
                    fee_to_use: clinicianFee,
                    fee_type: FEE_TYPE_BY_FIELD[field],
                    fee_source: "clinician",
                };
            }

            const generalFee = generalFees?.[field];
            if (generalFee) {
                return {
                    fee_to_use: generalFee,
                    fee_type: FEE_TYPE_BY_FIELD[field],
                    fee_source: "general",
                };
            }
        }

        return {
            fee_to_use: "",
            fee_type: FEE_TYPE_BY_FIELD[fields[fields.length - 1]],
            fee_source: "general",
        };
    };

    const isPsychFee =
        isSameString(requestedService, "Medication Management") ||
        isSameString(requestedService, "Psychiatric Counseling");
    const isCareerFee = isSameString(requestedService, "Career Assessment");
    const isGiftedAssessmentFee = isSameString(
        requestedService,
        "Gifted Assessment"
    );
    const isNutritionFee = isSameString(requestedService, "Nutrition Counseling");
    const isShortTermEvaluationDocumentationFee =
        isSameString(
            requestedService,
            "Short Term Evaluations & Documentation Services"
        ) ||
        isSameString(requestedService, "Short Term Evaluations & Documentation") ||
        isSameString(
            requestedService,
            "Short Term Evaluation & Documentation Services"
        ) ||
        isSameString(requestedService, "Short Term Evaluation & Documentation");
    const isKapFee =
        isSameString(requestedService, "Ketamine Assisted Psychotherapy") ||
        isSameString(
            requestedService,
            "Ketamine Assisted Psychotherapy (Evaluation)"
        ) ||
        isSameString(
            requestedService,
            "Ketamine Assisted Psychotherapy (Therapy Intake)"
        );
    const isInternExternTestingFee =
        isSameString(requestedService, "Mental Health Assessment: Intern") ||
        isSameString(requestedService, "Mental Health Assessment: Extern");
    const isTestingFee =
        (isSameString(
            requestedService,
            "Mental Health Assessment: Fully In-Person"
        ) ||
            isSameString(
                requestedService,
                "Mental Health Assessment: Partial In-Person"
            ) ||
            isSameString(requestedService, "Mental Health Assessment: Virtual")) &&
        credential !== "Extern";
    
        
    const PNP_PSYCH_FEE_FIELDS: Record<string, FeeField[]> = {
        "PNP-A": ["psychiatry_fee_a", "psychiatry_fee", "masters_fee"],
        "PNP-B": ["psychiatry_fee_b", "psychiatry_fee_a", "psychiatry_fee", "masters_fee"],
        "PNP-C": ["psychiatry_fee_c", "psychiatry_fee_b", "psychiatry_fee_a", "psychiatry_fee", "masters_fee"],
    };
    
    function getPsychFeeFields(credential: string): FeeField[] {
        return PNP_PSYCH_FEE_FIELDS[credential] || ["psychiatry_fee", "masters_fee"];
    }
        
    if (isPsychFee) {
        const psychFields = getPsychFeeFields(credential);
        const result = getFeeResult(psychFields);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (isInternExternTestingFee) {
        const result = getFeeResult(["intern_extern_testing_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (isTestingFee) {
        const result = getFeeResult(["testing_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (isKapFee) {
        const result = getFeeResult(["kap_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (isCareerFee) {
        const result = getFeeResult(["career_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (isGiftedAssessmentFee) {
        const result = getFeeResult(["gifted_assessment_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (isShortTermEvaluationDocumentationFee) {
        const result = getFeeResult(["short_term_evaluation_documentation_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (department_fee_type === "INN_FEES" && !isNutritionFee) {
        const result = getFeeResult(["inn_masters_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else if (department_fee_type === "INN_FEES" && isNutritionFee) {
        const result = getFeeResult(["inn_nutrition_fee"]);
        fee_to_use = result.fee_to_use;
        fee_type = result.fee_type;
        fee_source_used = result.fee_source;
    } else {
        switch (credential) {
            case "PhD":
            case "PsyD":
            case "MD":
                {
                    const result = getFeeResult(["dr_fee", "masters_fee"]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "PsyD-A":
            case "PhD-A":
                {
                    const result = getFeeResult([
                        "dr_fee_a",
                        "dr_fee",
                        "masters_fee",
                    ]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "PsyD-B":
            case "PhD-B":
                {
                    const result = getFeeResult([
                        "dr_fee_b",
                        "dr_fee_a",
                        "dr_fee",
                        "masters_fee",
                    ]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "PsyD-C":
            case "PhD-C":
                {
                    const result = getFeeResult([
                        "dr_fee_c",
                        "dr_fee_b",
                        "dr_fee_a",
                        "dr_fee",
                        "masters_fee",
                    ]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "Intern":
                {
                    const result = getFeeResult(["intern_fee"]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;
            case "Extern":
                {
                    const result = getFeeResult(["extern_fee"]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;
            case "LMHC":
            case "LCSW":
                {
                    const result = getFeeResult(["lic_fee", "masters_fee"]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "RDN":
                {
                    const result = getFeeResult(["nutrition_fee"]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "LMHC-A":
            case "LCSW-A":
                {
                    const result = getFeeResult([
                        "lic_a_fee",
                        "lic_fee",
                        "masters_fee",
                    ]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "LMHC-B":
            case "LCSW-B":
                {
                    const result = getFeeResult([
                        "lic_b_fee",
                        "lic_a_fee",
                        "lic_fee",
                        "masters_fee",
                    ]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "PNP-A":
            case "PNP-B":
            case "PNP-C":
                {
                    const result = getFeeResult(getPsychFeeFields(credential));
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            case "LMHC-C":
            case "LCSW-C":
                {
                    const result = getFeeResult([
                        "lic_c_fee",
                        "lic_b_fee",
                        "lic_a_fee",
                        "lic_fee",
                        "masters_fee",
                    ]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;

            default:
                {
                    const result = getFeeResult(["masters_fee"]);
                    fee_to_use = result.fee_to_use;
                    fee_type = result.fee_type;
                    fee_source_used = result.fee_source;
                }
                break;
        }
    }
    return {
        fee_type,
        fee_to_use,
        fee_source: fee_source_used,
    };
};

// For each tiered credential, the fee_type values that indicate a lower-tier
// fallback was used. Special-service fee types (Testing Fee, Kap Fee, etc.)
// are intentionally excluded — those bypass the tier chain entirely and should
// never be treated as a downgrade.
const LOWER_TIER_FEE_TYPES_BY_CREDENTIAL: Record<string, string[]> = {
    "PsyD-A": [FEE_TYPE_BY_FIELD.dr_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PhD-A": [FEE_TYPE_BY_FIELD.dr_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PsyD-B": [FEE_TYPE_BY_FIELD.dr_fee_a, FEE_TYPE_BY_FIELD.dr_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PhD-B": [FEE_TYPE_BY_FIELD.dr_fee_a, FEE_TYPE_BY_FIELD.dr_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PsyD-C": [FEE_TYPE_BY_FIELD.dr_fee_b, FEE_TYPE_BY_FIELD.dr_fee_a, FEE_TYPE_BY_FIELD.dr_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PhD-C": [FEE_TYPE_BY_FIELD.dr_fee_b, FEE_TYPE_BY_FIELD.dr_fee_a, FEE_TYPE_BY_FIELD.dr_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "LMHC-A": [FEE_TYPE_BY_FIELD.lic_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "LCSW-A": [FEE_TYPE_BY_FIELD.lic_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "LMHC-B": [FEE_TYPE_BY_FIELD.lic_a_fee, FEE_TYPE_BY_FIELD.lic_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "LCSW-B": [FEE_TYPE_BY_FIELD.lic_a_fee, FEE_TYPE_BY_FIELD.lic_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "LMHC-C": [FEE_TYPE_BY_FIELD.lic_b_fee, FEE_TYPE_BY_FIELD.lic_a_fee, FEE_TYPE_BY_FIELD.lic_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "LCSW-C": [FEE_TYPE_BY_FIELD.lic_b_fee, FEE_TYPE_BY_FIELD.lic_a_fee, FEE_TYPE_BY_FIELD.lic_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PNP-A": [FEE_TYPE_BY_FIELD.psychiatry_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PNP-B": [FEE_TYPE_BY_FIELD.psychiatry_fee_a, FEE_TYPE_BY_FIELD.psychiatry_fee, FEE_TYPE_BY_FIELD.masters_fee],
    "PNP-C": [FEE_TYPE_BY_FIELD.psychiatry_fee_b, FEE_TYPE_BY_FIELD.psychiatry_fee_a, FEE_TYPE_BY_FIELD.psychiatry_fee, FEE_TYPE_BY_FIELD.masters_fee],
};

export function isFeeDowngraded(credential: string, fee_type: string): boolean {
    const lowerTierTypes = LOWER_TIER_FEE_TYPES_BY_CREDENTIAL[credential.trim()];
    if (!lowerTierTypes) return false;
    return lowerTierTypes.includes(fee_type);
}

export function getFeesFromClient(
    clinician: Pick<IClinician, "credential" | "email">,
    client: Pick<IClient, ClientStatus.FEE_SETTING | "clinician_fees">,
    requestedService?: string,
    practiceId?: string
) {
    const credential = clinician.credential.trim();
    const fee_setting = client[ClientStatus.FEE_SETTING];
    const department_fee_type =
        practiceId === process.env.REACT_APP_MANTLE_PH_PRACTICE_ID
            ? "INN FEES"
            : "ONN FEES";
    const { fee_to_use } = getApplicableFee(
        client,
        credential,
        clinician.email,
        requestedService,
        department_fee_type
    );
    const fees = calculateFees(fee_to_use);
    return { credential, fees, fee_setting, fee_to_use };
}

export function calculateFees(
    fee: string,
    label?: string,
    department_fee_type?: string
): IFeeCalculation | null {
    // Formats:
    // 1. 80x5; $15 TBD
    // 2. 120TBD
    // 3. 25
    // 4. 120x7; $90
    // 5. Psych Fee formats:
    //    a) 455x1; 255 (evaluation + ongoing)
    //    b) 655x1; 455 UDM, then 80 (evaluation + UDM + ongoing)
    //    c) 165.76x2; 160.89 UDM, then 32.18 (evaluation with multiple sessions + UDM + ongoing)
    //    d) 452 UDM, then 22 (UDM + ongoing)
    // 6. Single psych fee: 16 or 16.09 (per session)
    // 7. NOT ACCEPTED
    // 8. 200; 100; 1000X2 THEN 200
    // 9. 200; 100; 1000

    const raw_fee = `${fee}`;
    let tbd_fee = "";
    let temp_no_of_sessions = "";
    let temp_fee = "";
    let single_fee = "";
    let evaluation_fee = "";
    let udm_fee = "";
    let ongoing_fee = "";
    let single_psych_fee = "";

    if (!raw_fee) {
        return null;
    }

    try {
        const unspaced_fee = raw_fee
            .split(" ")
            .map((f) => f.trim())
            .join("");

        // KAP Fee structured formats
        if (label === "Kap Fee" || label === "kap_fee") {
            const kapPatternWithThen =
                /^\s*(\d+(?:\.\d+)?)\s*;\s*(\d+(?:\.\d+)?)\s*;\s*(\d+(?:\.\d+)?)x(\d+)\s+then\s+(\d+(?:\.\d+)?)\s*$/i;
            const kapPatternSimple =
                /^\s*(\d+(?:\.\d+)?)\s*;\s*(\d+(?:\.\d+)?)\s*;\s*(\d+(?:\.\d+)?)\s*$/;

            const kapMatchWithThen = raw_fee.match(kapPatternWithThen);
            if (kapMatchWithThen) {
                const [
                    ,
                    pnpEval,
                    therapyIntake,
                    kapInitial,
                    kapInitialSessions,
                    kapOngoing,
                ] = kapMatchWithThen;

                return {
                    single_fee,
                    temp_fee,
                    temp_no_of_sessions,
                    tbd_fee,
                    evaluation_fee,
                    udm_fee,
                    ongoing_fee,
                    single_psych_fee,
                    pnp_evaluation_fee: pnpEval,
                    therapy_intake_fee: therapyIntake,
                    kap_initial_fee: kapInitial,
                    kap_initial_sessions: kapInitialSessions,
                    kap_ongoing_fee: kapOngoing,
                };
            }

            const kapMatchSimple = raw_fee.match(kapPatternSimple);
            if (kapMatchSimple) {
                const [, pnpEval, therapyIntake, kapPerSession] = kapMatchSimple;

                return {
                    single_fee,
                    temp_fee,
                    temp_no_of_sessions,
                    tbd_fee,
                    evaluation_fee,
                    udm_fee,
                    ongoing_fee,
                    single_psych_fee,
                    pnp_evaluation_fee: pnpEval,
                    therapy_intake_fee: therapyIntake,
                    kap_ongoing_fee: kapPerSession,
                };
            }
        }

        // Check for single psych fee format first (only for psych fees)
        if (
            label?.startsWith("Psych") ||
            label?.startsWith("psychiatry_fee") ||
            department_fee_type === "INN FEES"
        ) {
            // Check if it's a simple single psych fee (e.g., "16" or "16.09")
            const single_psych_match = /^(\d+(?:\.\d+)?)$/.exec(raw_fee);
            if (single_psych_match) {
                single_psych_fee = single_psych_match[1];
                return {
                    single_fee,
                    temp_fee,
                    temp_no_of_sessions,
                    tbd_fee,
                    evaluation_fee,
                    udm_fee,
                    ongoing_fee,
                    single_psych_fee,
                };
            }
        }

        // Check for TBD fees first
        const tbd_fees = TBD_FEE_REGEX.exec(unspaced_fee);
        if (tbd_fees) {
            tbd_fee = tbd_fees[0].replace(/TBD/i, "").replace("$", "");
        }

        // Check for Psych Fee specific formats (only if it's Psych Fee type)
        if (
            label?.startsWith("Psych") ||
            label?.startsWith("psychiatry_fee") ||
            department_fee_type === "INN FEES"
        ) {
            // Check for UDM + ongoing format (no evaluation) - e.g., "452 UDM, then 22"
            const udm_ongoing_match = raw_fee.match(
                /^(\d+(?:\.\d+)?)\s+UDM,\s*then\s+(\d+(?:\.\d+)?)$/
            );
            if (udm_ongoing_match) {
                udm_fee = udm_ongoing_match[1];
                ongoing_fee = udm_ongoing_match[2];
                return {
                    single_fee,
                    temp_fee,
                    temp_no_of_sessions,
                    tbd_fee,
                    evaluation_fee,
                    udm_fee,
                    ongoing_fee,
                };
            }

            // Check for evaluation + UDM + ongoing format
            const udm_match = UDM_FEE_REGEX.exec(raw_fee);
            const ongoing_match = ONGOING_FEE_REGEX.exec(raw_fee);

            if (udm_match || ongoing_match) {
                // Format: 655x1; 455 UDM, then 80 OR 165.76x2; 160.89 UDM, then 32.18
                const evaluation_match = EVALUATION_FEE_REGEX.exec(raw_fee);
                if (evaluation_match) {
                    // Store the full evaluation string (e.g., "165.76x2") for proper parsing later
                    evaluation_fee = evaluation_match[0];
                }
                if (udm_match) {
                    udm_fee = udm_match[1];
                }
                if (ongoing_match) {
                    ongoing_fee = ongoing_match[1];
                }

                return {
                    single_fee,
                    temp_fee,
                    temp_no_of_sessions,
                    tbd_fee,
                    evaluation_fee,
                    udm_fee,
                    ongoing_fee,
                };
            }

            // Check for evaluation + ongoing format (without UDM)
            // Format: 455x1; 255
            const evaluation_simple_match = EVALUATION_FEE_REGEX.exec(raw_fee);
            const ongoing_simple_match = raw_fee.match(/;\s*(\d+(?:\.\d+)?)$/);

            if (evaluation_simple_match && ongoing_simple_match) {
                // Store the full evaluation string (e.g., "455x1") for proper parsing later
                evaluation_fee = evaluation_simple_match[0];
                ongoing_fee = ongoing_simple_match[1];

                return {
                    single_fee,
                    temp_fee,
                    temp_no_of_sessions,
                    tbd_fee,
                    evaluation_fee,
                    udm_fee,
                    ongoing_fee,
                };
            }
        }

        // Check for regular temporary fees (like 180x14; 130TBD)
        const temp_fees = TEMP_FEE_REGEX.exec(unspaced_fee);
        if (temp_fees) {
            let [tfee, no_of_sessions] = temp_fees[0].split(/x/i);
            temp_fee = tfee.replace("$", "");
            temp_no_of_sessions = no_of_sessions;
        }

        const single_fees = SINGLE_FEE_REGEX.exec(unspaced_fee);
        if (single_fees) {
            let [sfee] = single_fees;
            single_fee = sfee.replace("$", "");
        }

        const has_fee = Boolean(single_fee || temp_fee || tbd_fee);
        if (has_fee) {
            return { single_fee, temp_fee, temp_no_of_sessions, tbd_fee };
        }
    } catch (ex) {
        console.log("Cannot calculate fees", ex);
    }
    return null;
}
